import React, { useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Select, { FormatOptionLabelMeta } from "react-select";

import {
  getSchoolSelectorStyles,
  SchoolSelectOption,
} from "components/global/select-field-styles";
import {
  SchoolSelectorControl,
  SchoolSelectorEmptyMessage,
  SchoolSelectorField,
  SchoolSelectorSelectWrap,
} from "components/global/styles/SchoolSelector.inline.styles";
import { workspaceApi } from "pages/app/workspace/services";

const ALL_TEAMS_VALUE = "__all_teams__";

const formatTeamOptionLabel = (
  option: SchoolSelectOption,
  { context }: FormatOptionLabelMeta<SchoolSelectOption>,
) => {
  if (context === "value") {
    return option.label;
  }

  return option.menuLabel || option.label;
};

type TeamSelectorProps = {
  schoolId: string;
  teamId: string;
};

export const TeamSelector: React.FC<TeamSelectorProps> = ({ schoolId, teamId }) => {
  const history = useHistory();
  const location = useLocation();
  const teamsQuery = useQuery({
    queryKey: ["workspace-school-teams", schoolId],
    queryFn: () => workspaceApi.schoolTeams.list(schoolId),
    enabled: Boolean(schoolId),
  });

  const teams = useMemo(() => teamsQuery.data || [], [teamsQuery.data]);

  const teamOptions = useMemo<SchoolSelectOption[]>(() => {
    const options = teams.map((team: any) => ({
      // Keep the closed value short; show sport only in the menu.
      label: team.name || "Untitled team",
      menuLabel: team.sport_name ? `${team.name} · ${team.sport_name}` : team.name || "Untitled team",
      value: String(team.id),
    }));

    return [{ label: "All teams", value: ALL_TEAMS_VALUE }, ...options];
  }, [teams]);

  const selectedOption = useMemo(
    () => teamOptions.find((option) => option.value === String(teamId)) || null,
    [teamId, teamOptions],
  );

  const selectStyles = useMemo(() => getSchoolSelectorStyles(), []);
  const showEmptyState = !teamsQuery.isLoading && Boolean(schoolId) && teams.length === 0;

  const handleTeamChange = (nextTeamId: string) => {
    if (!nextTeamId || nextTeamId === ALL_TEAMS_VALUE) {
      history.push("/workspace/school-teams");
      return;
    }

    if (nextTeamId === String(teamId)) {
      return;
    }

    history.push({
      pathname: `/workspace/school-teams/${schoolId}/${nextTeamId}`,
      search: location.search,
      state: {
        backTo: "/workspace/school-teams",
      },
    });
  };

  if (!schoolId) {
    return null;
  }

  if (showEmptyState) {
    return <SchoolSelectorEmptyMessage>No teams available</SchoolSelectorEmptyMessage>;
  }

  return (
    <SchoolSelectorField>
      <SchoolSelectorControl $compact>
        <SchoolSelectorSelectWrap>
          <Select
            aria-label="Select team"
            classNamePrefix="team-selector"
            formatOptionLabel={formatTeamOptionLabel}
            inputId="team-selector"
            isClearable={false}
            isDisabled={teamsQuery.isLoading}
            isSearchable={teamOptions.length > 6}
            menuPlacement="auto"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            onChange={(option) => handleTeamChange(option?.value ?? "")}
            options={teamOptions}
            placeholder={teamsQuery.isLoading ? "Loading teams..." : "Select a team"}
            styles={selectStyles}
            value={selectedOption}
          />
        </SchoolSelectorSelectWrap>
      </SchoolSelectorControl>
    </SchoolSelectorField>
  );
};
